import React, {
  ChangeEventHandler,
  FocusEvent,
  forwardRef,
  HTMLAttributes,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { Error } from 'components';
import { t } from 'utils';

import styles from './FileUpload.module.scss';

export interface IFileUpload extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: any;
  label?: string;
  placeholder?: string;
  onChange?: (files: FileList) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  accept?: string;
  maxFiles: number;
  disabled?: boolean;
  error: string;
}

const FileUpload = forwardRef<HTMLInputElement, IFileUpload>((props, ref) => {
  const {
    label = '',
    placeholder = '',
    className,
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    multiple,
    accept,
    maxFiles,
    value,
    disabled,
    error,
  } = props;
  const rootClassNames = [classNames(styles.root, className, { [styles.disabled]: disabled })];
  const uploadButtonClasses = [styles.button];
  const filesUploaded = value.length || 0;
  const isUploadDisabled = filesUploaded >= maxFiles || !!disabled;
  if (isUploadDisabled) {
    uploadButtonClasses.push(styles.disabled);
    rootClassNames.push(styles.disabled);
  }

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const files = e.target.files;
      if (!files || !files.length) return;
      onChange(files);
    },
    [onChange]
  );

  return (
    <div className={rootClassNames.join(' ')}>
      <input
        id='upload'
        type='file'
        className='visually-hidden'
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={ref}
        multiple={multiple}
        accept={accept}
      />
      <label className={styles.container} htmlFor='upload'>
        {label && <span className={styles.label}>{t(label)}</span>}
        {placeholder && <span className={styles.placeholder}>{t(placeholder)}</span>}
        <span className={styles.button}>{t('browse')}</span>
      </label>
      {error && (
        <Error className='mt-1' isError={!!error}>
          {t(error)}
        </Error>
      )}
    </div>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
